import { NextComponentType } from 'next';
import { AppContext, AppInitialProps, AppProps } from 'next/app';
import Head from 'next/head';

import React, { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Provider } from 'react-wrap-balancer';

import { Noto_Sans_KR } from '@next/font/google';
import amplitude from 'amplitude-js';
import usePageViewEvent from 'hooks/usePageViewEvent';
import { useWebviewSignIn } from 'hooks/useWebviewSignIn';
import mixpanel from 'mixpanel-browser';
import moment from 'moment';
import { RecoilRoot } from 'recoil';
import { DefaultTheme, ThemeProvider } from 'styled-components';

import { TrackingScript } from '@components/tracking/scripts';

import { AMPLITUDE_KEY, GA4_KEY, MIXPANEL_KEY } from '@utils/constants';

import '@styles/globals.css';

const queryClient = new QueryClient();

const theme: DefaultTheme = {
  colors: {
    primary: '#055cf5',
  },
};

const noto = Noto_Sans_KR({
  subsets: ['latin'],
  weight: ['400', '500', '700'],
  display: 'swap',
});

const MyApp: NextComponentType<AppContext, AppInitialProps, AppProps> = ({ Component, pageProps, router }) => {
  usePageViewEvent(router);

  useEffect(() => {
    const appHeight = () => {
      const doc = document.documentElement;
      if (!doc.style.getPropertyValue('--initial-app-height'))
        doc.style.setProperty('--initial-app-height', `${window.innerHeight}px`);
      doc.style.setProperty('--app-height', `${window.innerHeight}px`);
    };
    window.addEventListener('resize', appHeight);
    appHeight();
    mixpanel.init(MIXPANEL_KEY);
    amplitude.getInstance().init(AMPLITUDE_KEY);
    ReactGA.initialize(GA4_KEY);

    if (process.env.NODE_ENV === 'production') {
      const buildDate = moment(process.env.BUILD_DATE).format('YYYY-MM-DD HH:mm:ss');
      console.log('build date: ', buildDate);
    }
  }, []);

  return (
    <RecoilRoot>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <Head>
            <meta
              name="viewport"
              content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover"
            />
            <meta name="google-site-verification" content="IoKDDAij5YZv6R6LOUaoRs1wSdy3EEoddDCYyzWOCyk" />
            <meta name="google-site-verification" content="fKquNQhiwRNggfsE_otWD-6VHHuY2oXl7HYCj6txREc" />
            {/* BORAWARE LOG SCRIPT */}
            <script
              type="text/javascript"
              dangerouslySetInnerHTML={{
                __html: `
                  var protect_id = 'g162';
                `,
              }}
            />
            <script async type="text/javascript" src="//script.boraware.kr/protect_script_v2.js" />
            {/* END OF BORAWARE LOG SCRIPT */}
          </Head>
          <TrackingScript />
          <Provider>
            <AppSignIn />
            <Component {...pageProps} />
          </Provider>
          <style jsx global>{`
            html,
            body {
              font-family: ${noto.style.fontFamily};
            }
          `}</style>
        </ThemeProvider>
      </QueryClientProvider>
    </RecoilRoot>
  );
};

export default MyApp;

const AppSignIn = () => {
  useWebviewSignIn();

  return null;
};
